import { useCallback, useMemo } from "react";

import { useAuthorisation } from "../hooks/useAuthorisation";

// API base URL defined outside component to avoid dependency issues
// Use relative URL for production (same domain), localhost for dev
const API_BASE_URL = process.env.REACT_APP_API || "/api";

export const useUserSessions = () => {
  const { user, getAccessTokenOrSignIn: getAccessToken } = useAuthorisation();

  // Memoize to satisfy ESLint deps
  const apiBaseUrl = useMemo(() => API_BASE_URL, []);

  const getUserSessions = useCallback(async () => {
    const sessionToken = await getAccessToken();

    if (!sessionToken) {
      console.error("[useUserSessions] No token obtained, aborting fetch.");
      throw new Error("Authentication token not available");
    }

    const response = await fetch(`${apiBaseUrl}/secure/sessions`, {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    });
    if (!response.ok) throw new Error("Failed to load sessions");
    return await response.json();
  }, [getAccessToken, apiBaseUrl]);

  const getUserSessionChat = useCallback(
    async (sessionId) => {
      const sessionToken = await getAccessToken();

      const response = await fetch(
        `${apiBaseUrl}/secure/sessions/${sessionId}/chat`,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to load chat");
      return await response.json();
    },
    [getAccessToken]
  );

  const postUserSessionChat = useCallback(
    async (sessionId, message, lastResults, options = {}) => {
      const sessionToken = await getAccessToken();

      if (!sessionToken) {
        return {
          messages: [],
        };
      }

      const { useStreaming = false } = options;

      const response = await fetch(
        `${apiBaseUrl}/secure/sessions/${sessionId}/chat`,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
          method: "POST",
          body: JSON.stringify({ message, lastResults, useStreaming }),
        }
      );
      if (!response.ok) throw new Error("Failed to post chat");
      return await response.json();
    },
    [getAccessToken]
  );

  const createUserSession = useCallback(async () => {
    const sessionToken = await getAccessToken();
    const response = await fetch(`${apiBaseUrl}/secure/sessions`, {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
      method: "POST",
      body: JSON.stringify({
        userId: user.id,
      }),
    });
    if (!response.ok) throw new Error("Failed to create session");
    return await response.json();
  }, [getAccessToken, user]);

  const deleteUserSession = useCallback(
    async (sessionId) => {
      const sessionToken = await getAccessToken();
      const response = await fetch(
        `${apiBaseUrl}/secure/sessions/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
          method: "DELETE",
        }
      );
      if (!response.ok) throw new Error("Failed to delete session");
      return await response.json();
    },
    [getAccessToken]
  );

  const getUserSessionSearchStatus = useCallback(
    async (sessionId) => {
      const sessionToken = await getAccessToken();

      const response = await fetch(
        `${apiBaseUrl}/secure/sessions/${sessionId}/search/status`,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to get search status");
      return await response.json();
    },
    [getAccessToken]
  );

  const startUserSessionSearch = useCallback(
    async (sessionId) => {
      const sessionToken = await getAccessToken();

      const response = await fetch(
        `${apiBaseUrl}/secure/sessions/${sessionId}/search/start`,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
          method: "POST",
        }
      );
      if (!response.ok) throw new Error("Failed to start search");
      return await response.json();
    },
    [getAccessToken]
  );

  const cancelUserSessionSearch = useCallback(
    async (sessionId) => {
      const sessionToken = await getAccessToken();

      return await fetch(
        `${apiBaseUrl}/secure/sessions/${sessionId}/search/cancel`,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
          method: "POST",
        }
      );
    },
    [getAccessToken]
  );

  const getUserSessionChatSummary = useCallback(
    async (sessionId) => {
      const sessionToken = await getAccessToken();

      return await fetch(
        `${apiBaseUrl}/secure/sessions/${sessionId}/chat/summarize`,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
          method: "POST",
        }
      );
    },
    [getAccessToken]
  );

  const getUserPreferences = useCallback(async () => {
    const sessionToken = await getAccessToken();
    return await fetch(`${apiBaseUrl}/secure/preferences`, {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    });
  }, [getAccessToken, apiBaseUrl]);

  const updateUserPreferences = useCallback(
    async (preferences) => {
      const sessionToken = await getAccessToken();
      return await fetch(`${apiBaseUrl}/secure/preferences`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        method: "PUT",
        body: JSON.stringify(preferences),
      });
    },
    [getAccessToken]
  );

  const sendQuery = useCallback(
    async (requirements, timestamp = new Date().toISOString()) => {
      const sessionToken = await getAccessToken();

      // Handle both string and object input for backwards compatibility
      let requestBody;

      if (typeof requirements === "string") {
        // Convert string to minimal requirements object
        requestBody = {
          query: requirements,
          requirements: { requirementsSummary: requirements },
          limit: 20,
          timestamp,
        };
        console.log("[sendQuery] Converting string requirements to object");
      } else {
        // Ensure we have a requirements object with requirementsSummary
        if (!requirements.requirementsSummary) {
          console.warn(
            "[sendQuery] Requirements object missing requirementsSummary"
          );
        }

        requestBody = {
          query: requirements.requirementsSummary || "",
          requirements: requirements, // Pass the full requirements object
          limit: 20,
          timestamp,
        };
      }

      const response = await fetch(`${apiBaseUrl}/secure/query`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        method: "POST",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(
          `Vector search failed: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    },
    [getAccessToken]
  );

  const queryVectorStore = useCallback(
    async (
      query,
      inferredPreferences,
      timestamp = new Date().toISOString()
    ) => {
      const sessionToken = await getAccessToken();

      if (!sessionToken) {
        return {
          data: {
            matches: [],
          },
        };
      }

      const response = await fetch(`${apiBaseUrl}/secure/query`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
        method: "POST",
        body: JSON.stringify({
          query,
          inferredPreferences,
          timestamp,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Vector search failed: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    },
    [getAccessToken]
  );

  return {
    isSignedIn: !!user,
    getUserSessions,
    createUserSession,
    getUserSessionChat,
    postUserSessionChat,
    getUserSessionSearchStatus,
    startUserSessionSearch,
    deleteUserSession,
    cancelUserSessionSearch,
    getUserSessionChatSummary,
    getUserPreferences,
    updateUserPreferences,
    sendQuery,
    queryVectorStore,
  };
};
